<template>
  <div class="intercom-date-input-wrapper">
    <label v-if="label" class="intercom-input-label">
      {{ label }}
      <span v-if="required" class="intercom-input-required">*</span>
    </label>
    <n-date-picker
      v-model:formatted-value="internalValue"
      value-format="yyyy-MM-dd"
      :format="displayFormat"
      type="date"
      :disabled="disabled"
      :is-date-disabled="isDateDisabled"
      :class="['intercom-date-picker', { 'intercom-date-picker--error': error }]"
      :placeholder="placeholder || 'DD-MM-YYYY'"
      clearable
      @update:formatted-value="handleUpdate"
      @keydown="$emit('keydown', $event)"
    />
    <p v-if="error" class="intercom-input-error">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { NDatePicker } from 'naive-ui'

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  error: { type: String, default: '' },
  min: { type: String, default: '' },
  displayFormat: { type: String, default: 'dd-MM-yyyy' },
})

const emit = defineEmits(['update:modelValue', 'keydown'])

const internalValue = ref(props.modelValue || null)

watch(() => props.modelValue, (val) => {
  internalValue.value = val || null
})

function handleUpdate(val) {
  emit('update:modelValue', val || '')
}

function isDateDisabled(ts) {
  if (!props.min) return false
  const minDate = new Date(props.min + 'T00:00:00')
  const d = new Date(ts)
  d.setHours(0, 0, 0, 0)
  return d < minDate
}
</script>

<style scoped>
.intercom-date-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.intercom-input-label {
  font-size: 13px;
  font-weight: 500;
  color: #1F1F1F;
  line-height: 1.2;
}

.intercom-input-required {
  color: #dc2626;
  margin-left: 2px;
}

.intercom-input-error {
  font-size: 12px;
  color: #dc2626;
  margin: 0;
}

/* Override Naive UI date picker to match Intercom design */
.intercom-date-picker {
  width: 100%;
}

:deep(.n-input) {
  --n-height: 36px !important;
  --n-border-radius: 8px !important;
  --n-font-size: 14px !important;
  --n-border: 1px solid #D1D1D1 !important;
  --n-border-hover: 1px solid #0057FF !important;
  --n-border-focus: 1px solid #0057FF !important;
  --n-box-shadow-focus: 0 0 0 2px rgba(0, 87, 255, 0.15) !important;
}

:deep(.n-input .n-input__input-el) {
  font-size: 14px !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
}

.intercom-date-picker--error :deep(.n-input) {
  --n-border: 1px solid #dc2626 !important;
  --n-border-hover: 1px solid #dc2626 !important;
}
</style>
